import React, { useState } from 'react';
import { Query } from '../_common';
import SearchBar from '../SearchBar';
import { SearchTypeEnum } from '../../types/SearchTypeEnum';
import { ModalForm, Option } from './interface';
import { GET_USER_FILTERED, GET_ROLES } from '../../graphql/client/queries';
import {
    GetRoles,
    GetRoles_getRoles as Role,
    GetUserFiltered_getUserFiltered as User,
    GetUserFiltered,
} from '../../types/schemaTypes';
import UserList from './UserList';
import { Error, Loading } from '../_common';
import AddUserModal from './AddEditUserModal/AddEditUserModal';
import './Users.scss';
import { useQuery } from '@apollo/client/react';

import { useStoreContext } from '../App/StoreProvider';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { useMutation } from '@apollo/client/react';
import { SAVE_USER } from '../../graphql/client/mutations';
import { SaveUser } from '../../types/schemaTypes';

let selectedUser: User | undefined;

const PAGE_SIZE = 50;

const Users = (): React.ReactElement => {
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [modalForm, setModalForm] = useState(ModalForm.EMPTY);
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [tabTitle, setTabTitle] = useState(document.title);
    document.title = 'SBS';
    const canEditUsers = useAuthContext().userHasPermission(PermissionEnum.EditUsers);
    const [state] = useStoreContext();
    const {
        userList: { searchPhrase },
    } = state;

    const variables = {
        input: searchPhrase,
    };

    const onCompletedSaveUser = (data: SaveUser): void => {
        if (data.saveUser === null) return;
        const saveUserErrors = data.saveUser.errors;
        if (saveUserErrors && saveUserErrors.length > 0) {
            setErrorMessage(saveUserErrors[0] || '');
        } else {
            selectedUser = undefined;
            setShowAddUserModal(false);
        }
    };

    const [saveUser] = useMutation<SaveUser>(SAVE_USER, {
        onCompleted: onCompletedSaveUser,
        refetchQueries: () => {
            return [
                {
                    query: GET_USER_FILTERED,
                    variables: { variables },
                },
            ];
        },
    });

    const handleOnHide = (): void => {
        selectedUser = undefined;
        setErrorMessage('');
        setShowAddUserModal(false);
    };
    const handleOnSave = async (
        id: number,
        name: string,
        email: string,
        roleId: number,
        status: boolean,
        regionIds: string,
        districtIds: string,
    ): Promise<void> => {
        if (selectedUser) {
            const selectedUserMirror = {...selectedUser}
            selectedUserMirror.name = name;
            selectedUserMirror.emailAddress = email;
            selectedUserMirror.roleId = roleId;
            selectedUserMirror.isActive = status;
            selectedUser = {...selectedUserMirror}
        }

        const variables = {
            input: {
                id,
                name,
                emailAddress: email,
                roleId,
                isActive: status,
                regionIds: regionIds,
                districtIds: districtIds,
            },
        };
        await saveUser({ variables });
    };

    const { data: rolesData } = useQuery<GetRoles>(GET_ROLES);

    const options: Option[] = [];

    if (rolesData && rolesData.getRoles) {
        rolesData.getRoles.forEach((element: Role | null) => {
            if (!element || element.id === null) return;
            const id = element.id;
            const displayName = element.displayName ?? '<None>';
            options.push({ id, name: displayName });
        });
    }
    document.title = 'Users';
    return (
        <Query<GetUserFiltered> query={GET_USER_FILTERED} variables={variables}>
            {({
                error,
                data,
                loading,
                refetch,
                fetchMore,
            }): React.ReactElement | null => {

                const offset = data && data.getUserFiltered ? data.getUserFiltered.length / PAGE_SIZE : 0;

                React.useEffect(()=>{
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults && data && data.getUserFiltered && data.getUserFiltered.length < PAGE_SIZE)
                    ) {
                        // If offset is not a whole number, the end of the results has been reached. (8/22/19) - BS
                        setIsEndOfResults(true);
                    }
                },[offset, data?.getUserFiltered?.length]);
                
                if (loading) {
                    return <Loading />;
                }

                if (error) {
                    return <Error error={error} />;
                }

                const users: User[] = [];

                if (data && data.getUserFiltered && data.getUserFiltered.length !== 0) {
                    data.getUserFiltered.forEach((element) => {
                        if (element !== null) users.push(element as User);
                    });
                }

                return (
                    <div className="Users-Container">
                        <div className="Users-Top-Container">
                            <div className="Users-SearchBar">
                                <SearchBar type={SearchTypeEnum.User}></SearchBar>
                            </div>
                            <div className="Users-RightOptions">
                                {canEditUsers && (
                                    <div
                                        className="Users-Create-New-User Row"
                                        onClick={() => {
                                            setShowAddUserModal(true);
                                            setModalForm(ModalForm.ADD_USER);
                                        }}
                                    >
                                        <i className="material-icons Users-Icon">add_circle</i>
                                        <span className="Users-Link">Create New User</span>
                                    </div>
                                )}
                            </div>
                            <div className="Users-Errors">{errorMessage}</div>
                        </div>
                        <div className="Users-Bottom-Container">
                            <div className="Users-Header-Title">Name</div>
                            <div className="Users-Header-Title">Email</div>
                            <div className="Users-Header-Title">Role</div>
                            <div className="Users-Header-Title">Status</div>
                            <div className="Users-Header-Title">Actions</div>
                        </div>
                        {users.length > 0 && (
                            <UserList
                                users={users}
                                refetch={refetch}
                                options={options}
                                isLoading={loading}
                                canEditUsers={canEditUsers}
                                isEndOfResults={isEndOfResults}
                                onEditClick={user => {
                                    setShowAddUserModal(true);
                                    setModalForm(ModalForm.EDIT_USER);
                                    selectedUser = user;
                                }}
                                onLoadMore={() =>
                                    fetchMore({
                                        variables: {
                                            offset,
                                        },
                                        updateQuery: (prev, { fetchMoreResult }) => {
                                            if (
                                                prev &&
                                                fetchMoreResult &&
                                                prev.getUserFiltered &&
                                                fetchMoreResult.getUserFiltered
                                            ) {
                                                return Object.assign({}, prev, {
                                                    getUserFiltered: [
                                                        ...prev.getUserFiltered,
                                                        ...fetchMoreResult.getUserFiltered,
                                                    ],
                                                });
                                            } else {
                                                return prev;
                                            }
                                        },
                                    })
                                }
                            />
                        )}
                        {showAddUserModal && (
                            <AddUserModal
                                show={showAddUserModal}
                                modalForm={modalForm}
                                options={options}
                                selectedUser={selectedUser}
                                onClickHide={handleOnHide}
                                onClickSave={handleOnSave}
                                errorMessage={errorMessage}
                                userId={selectedUser?.id == null ? 0 : selectedUser.id}
                            />
                        )}
                    </div>
                );
            }}
        </Query>
    );
};

export default Users;

